import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  User, Envelope, Buildings, Briefcase, Calendar, ShieldCheck, Key, Cube,
  ArrowLeft, SpinnerGap, Warning, Lightning, ClipboardText, PaperPlaneTilt,
  CheckCircle, XCircle, Clock, ArrowsLeftRight, PencilSimple, Timer,
  ShieldWarning, CaretRight, UserMinus
} from '@phosphor-icons/react';

import Breadcrumbs from '../components/Breadcrumbs';

const RISK_COLORS = {
  critical: { bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-500' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', ring: 'ring-orange-500' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-400' },
  low: { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-400' },
};

const TIMELINE_ICONS = {
  identity: PencilSimple,
  role: ShieldCheck,
  request: PaperPlaneTilt,
  jit: Lightning,
  certification: ClipboardText,
  lifecycle: ArrowsLeftRight,
};

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

export default function Identity360Page() {
  const { identityId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [identityId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/identities/${identityId}/360`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load identity data');
      navigate('/identities');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[500px]">
        <SpinnerGap size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  const { identity, manager, role_assignments, entitlements, applications, pending_requests, active_jit, certifications, certification_items, sod_violations, risk, timeline, summary } = data;
  const rc = RISK_COLORS[risk.level] || RISK_COLORS.medium;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="identity-360-page">
      {/* Breadcrumbs + Header */}
      <div>
        <Breadcrumbs items={[
          { label: 'Identities', path: '/identities' },
          { label: identity.display_name }
        ]} />

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-navy-100 flex items-center justify-center text-xl font-bold text-navy-600">
              {identity.display_name?.charAt(0) || '?'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-navy-900" data-testid="identity-name">{identity.display_name}</h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-navy-500">
                {identity.job_title && <span className="flex items-center gap-1"><Briefcase size={12} /> {identity.job_title}</span>}
                {identity.department && <span className="flex items-center gap-1"><Buildings size={12} /> {identity.department}</span>}
                {identity.email && <span className="flex items-center gap-1"><Envelope size={12} /> {identity.email}</span>}
              </div>
              <div className="flex gap-2 mt-2">
                <Badge className={`text-[10px] ${identity.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}`}>
                  {identity.status || 'active'}
                </Badge>
                <Badge className="text-[10px] bg-slate-100 text-navy-600">{identity.lifecycle_state || 'active'}</Badge>
                <Badge className="text-[10px] bg-slate-100 text-navy-600">{identity.source || 'manual'}</Badge>
                {summary.is_orphaned && <Badge className="text-[10px] bg-amber-100 text-amber-700">Orphaned</Badge>}
              </div>
            </div>
          </div>

          {/* Risk Score */}
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${rc.bg}`} data-testid="identity-risk-score">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ring-2 ${rc.ring} ${rc.bg}`}>
              <span className={`text-lg font-black ${rc.text}`}>{risk.score}</span>
            </div>
            <div>
              <p className={`text-sm font-bold ${rc.text} capitalize`}>{risk.level} Risk</p>
              <p className="text-[10px] text-navy-500">{Object.values(risk.factors).filter(v => v && v !== 0 && v !== false).length} active factors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3" data-testid="identity-stats">
        {[
          { label: 'Roles', value: summary.total_roles, icon: ShieldCheck, color: 'text-blue-600' },
          { label: 'Entitlements', value: summary.total_entitlements, icon: Key, color: 'text-amber-600' },
          { label: 'Applications', value: summary.total_applications, icon: Cube, color: 'text-purple-600' },
          { label: 'Accounts', value: summary.total_accounts, icon: User, color: 'text-navy-600' },
          { label: 'Pending', value: summary.pending_requests, icon: PaperPlaneTilt, color: 'text-orange-600' },
          { label: 'Active JIT', value: summary.active_jit, icon: Lightning, color: 'text-emerald-600' },
          { label: 'SoD Issues', value: summary.sod_violations, icon: Warning, color: 'text-red-600' },
          { label: 'Cert Reviews', value: certifications.total, icon: ClipboardText, color: 'text-teal-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-3 text-center" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.icon size={16} weight="duotone" className={`mx-auto ${s.color}`} />
            <p className="text-lg font-bold text-navy-900 mt-1">{s.value}</p>
            <p className="text-[9px] text-navy-500 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* LEFT COLUMN: Profile + Manager + Risk Factors */}
        <div className="space-y-4">
          {/* Manager */}
          {manager && (
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="identity-manager">
              <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider mb-2">Reports To</p>
              <div className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 rounded-lg p-2 -m-2 transition-colors"
                   onClick={() => navigate(`/identities/${manager.id}`)}>
                <div className="w-9 h-9 rounded-full bg-navy-100 flex items-center justify-center text-sm font-bold text-navy-600">
                  {manager.display_name?.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-navy-900">{manager.display_name}</p>
                  <p className="text-xs text-navy-400">{manager.job_title || manager.email || ''}</p>
                </div>
                <CaretRight size={12} className="ml-auto text-navy-300" />
              </div>
            </div>
          )}

          {/* Risk Factors */}
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="identity-risk-factors">
            <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider mb-3 flex items-center gap-1">
              <ShieldWarning size={12} /> Risk Breakdown
            </p>
            <div className="space-y-2">
              {[
                { label: 'Privileged Roles', value: risk.factors.privileged_roles, bad: risk.factors.privileged_roles > 0 },
                { label: 'SoD Violations', value: risk.factors.sod_violations, bad: risk.factors.sod_violations > 0 },
                { label: 'Days Since Cert', value: risk.factors.days_since_certification ?? 'Never', bad: !risk.factors.days_since_certification || risk.factors.days_since_certification > 90 },
                { label: 'Orphaned', value: risk.factors.orphaned ? 'Yes' : 'No', bad: risk.factors.orphaned },
                { label: 'Active JIT', value: risk.factors.active_jit_sessions, bad: risk.factors.active_jit_sessions > 0 },
              ].map(f => (
                <div key={f.label} className="flex items-center justify-between text-xs py-1">
                  <span className="text-navy-600">{f.label}</span>
                  <span className={`font-semibold ${f.bad ? 'text-red-600' : 'text-navy-500'}`}>{String(f.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* SoD Violations */}
          {sod_violations.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="identity-sod">
              <p className="text-[10px] text-red-700 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <Warning size={12} /> SoD Violations ({sod_violations.length})
              </p>
              {sod_violations.map(v => (
                <div key={v.id} className="text-xs text-red-800 py-1 border-b border-red-100 last:border-0">
                  {v.policy_name}
                </div>
              ))}
            </div>
          )}

          {/* Certification Summary */}
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="identity-cert-summary">
            <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider mb-3 flex items-center gap-1">
              <ClipboardText size={12} /> Certification Status
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-emerald-600">{certifications.approved}</p>
                <p className="text-[9px] text-navy-400 uppercase">Approved</p>
              </div>
              <div>
                <p className="text-lg font-bold text-amber-600">{certifications.pending}</p>
                <p className="text-[9px] text-navy-400 uppercase">Pending</p>
              </div>
              <div>
                <p className="text-lg font-bold text-red-600">{certifications.revoked}</p>
                <p className="text-[9px] text-navy-400 uppercase">Revoked</p>
              </div>
            </div>
            {certifications.last_reviewed && (
              <p className="text-[10px] text-navy-400 mt-2 text-center">Last reviewed: {new Date(certifications.last_reviewed).toLocaleDateString()}</p>
            )}
          </div>
        </div>

        {/* MIDDLE COLUMN: Roles + Entitlements + Applications */}
        <div className="space-y-4">
          {/* Role Assignments */}
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="identity-roles">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck size={12} /> Roles ({summary.total_roles})
              </p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[250px] overflow-y-auto">
              {role_assignments.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-navy-400">No role assignments</div>
              ) : role_assignments.map(ra => (
                <div key={ra.id} className="px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-navy-900">{ra.role_name}</p>
                    <p className="text-[10px] text-navy-400">{ra.assignment_type || 'manual'} &middot; {ra.assigned_by}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {ra.expires_at && (
                      <span className="text-[10px] text-amber-600 flex items-center gap-0.5"><Clock size={10} /> {new Date(ra.expires_at).toLocaleDateString()}</span>
                    )}
                    <Badge className={`text-[9px] ${ra.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-500'}`}>
                      {ra.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Entitlements */}
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="identity-entitlements">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider flex items-center gap-1">
                <Key size={12} /> Entitlements ({summary.total_entitlements})
              </p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
              {entitlements.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-navy-400">No entitlements</div>
              ) : entitlements.map(e => (
                <div key={e.id} className="px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key size={12} className="text-amber-500 shrink-0" />
                    <p className="text-sm text-navy-800 truncate">{e.name}</p>
                  </div>
                  <Badge className="text-[9px] bg-slate-100 text-navy-500">{e.type}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Applications */}
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="identity-applications">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider flex items-center gap-1">
                <Cube size={12} /> Applications ({summary.total_applications})
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {applications.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-navy-400">No applications</div>
              ) : applications.map(a => (
                <div key={a.id} className="px-4 py-2.5 flex items-center gap-2 cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/app-accounts/${a.id}`)}>
                  <Cube size={14} className="text-purple-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-900 truncate">{a.name}</p>
                    <p className="text-[10px] text-navy-400">{a.type || 'manual'}</p>
                  </div>
                  <CaretRight size={12} className="text-navy-300" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: JIT + Pending + Timeline */}
        <div className="space-y-4">
          {/* Active JIT Sessions */}
          {active_jit.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="identity-jit">
              <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <Lightning size={12} weight="fill" /> Active JIT Sessions ({active_jit.length})
              </p>
              {active_jit.map(j => (
                <div key={j.id} className="flex items-center justify-between py-1.5 border-b border-emerald-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-emerald-900">{j.role_name}</p>
                    <p className="text-[10px] text-emerald-600">{j.duration_hours}h session</p>
                  </div>
                  <Badge className={`text-[9px] ${j.status === 'active' ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-200 text-amber-800'}`}>
                    {j.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          {/* Pending Requests */}
          {pending_requests.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="identity-pending">
              <p className="text-[10px] text-orange-700 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <PaperPlaneTilt size={12} /> Pending Requests ({pending_requests.length})
              </p>
              {pending_requests.map(r => (
                <div key={r.id} className="text-xs text-orange-800 py-1.5 border-b border-orange-100 last:border-0">
                  {(r.items || []).map(i => i.name).join(', ') || 'Access request'} &middot; {timeAgo(r.created_at)}
                </div>
              ))}
            </div>
          )}

          {/* Activity Timeline */}
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="identity-timeline">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-wider flex items-center gap-1">
                <Clock size={12} /> Recent Activity ({timeline.length})
              </p>
            </div>
            <div className="px-4 py-2 max-h-[350px] overflow-y-auto">
              {timeline.length === 0 ? (
                <div className="py-6 text-center text-xs text-navy-400">No recent activity</div>
              ) : (
                <div className="relative pl-5">
                  <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
                  {timeline.map((evt, idx) => {
                    const Icon = TIMELINE_ICONS[evt.type] || PencilSimple;
                    return (
                      <div key={evt.id || `timeline-${evt.timestamp}-${idx}`} className="relative flex gap-2 pb-4" data-testid={`timeline-item-${idx}`}>
                        <div className="absolute -left-5 w-[16px] h-[16px] rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                          <Icon size={8} weight="bold" className="text-navy-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-navy-800 truncate">{evt.title}</p>
                          <p className="text-[10px] text-navy-400">{evt.actor} &middot; {timeAgo(evt.timestamp)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
